import { Checkbox, Select, Textarea, TextInput } from '@mantine/core';
import { Controller } from 'react-hook-form';
import type { FieldDescriptor, FieldRegistry, LayoutDocument } from './types';
import type { ObligationInputSchema } from '../schemas';

/**
 * Obligation edit-form field registry. Mirrors
 * api/src/lib/obligation-form-layout-schema.ts — the field-name list below
 * MUST stay in sync with that file's `OBLIGATION_FORM_FIELD_NAMES` (15
 * fields).
 *
 * Unlike wells/leases/contracts, obligations have NO reviewer_user_id and no
 * lookup-backed selects — every field is self-contained (text/checkbox/a
 * static 3-value select/textarea), so the context bag is empty, same as
 * tract-fields.tsx.
 */

export const OBLIGATION_FORM_FIELD_NAMES = [
  'name',

  'applies_to_lease',
  'applies_to_contract',
  'applies_to_well',

  'value_type',

  'attention',
  'address_1',
  'address_2',
  'city',
  'state',
  'zip',
  'phone',
  'fax',
  'email',

  'remarks',
] as const;

export type ObligationFormFieldName = (typeof OBLIGATION_FORM_FIELD_NAMES)[number];

export type ObligationFormContext = Record<string, never>;

type ObligationFieldDescriptor = FieldDescriptor<ObligationInputSchema, ObligationFormContext>;

function textProps(
  descriptor: { label: string; placeholder?: string },
  disabled: boolean,
  required = false,
) {
  const props: { label: string; placeholder?: string; disabled?: boolean; required?: boolean } = {
    label: descriptor.label,
  };
  if (descriptor.placeholder !== undefined) props.placeholder = descriptor.placeholder;
  if (disabled) props.disabled = true;
  if (required) props.required = true;
  return props;
}

function textField(
  label: string,
  defaultSpan: number,
  opts: { placeholder?: string; required?: boolean } = {},
): ObligationFieldDescriptor {
  const desc: ObligationFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <TextInput {...textProps(descriptor, disabled, opts.required)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function textareaField(label: string, defaultSpan: number): ObligationFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <Textarea autosize minRows={2} {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
}

function checkboxField(label: string, defaultSpan: number): ObligationFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Checkbox
            label={descriptor.label}
            checked={(field.value as boolean | null) ?? false}
            onChange={(e) => field.onChange(e.currentTarget.checked)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

const VALUE_TYPE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'date', label: 'Date' },
  { value: 'days', label: 'Days' },
];

/** Static 3-value Select (spec §2.1: `value_type` CHECK IN ('none','date','days')) —
 * what value an attachment of this obligation carries. Unlike the
 * lookup-backed selects on wells/leases/contracts, this is a fixed enum, not
 * a project-seeded lookup, so it needs no context. */
const valueTypeField: ObligationFieldDescriptor = {
  label: 'Value type',
  defaultSpan: 6,
  render: ({ name, control, disabled }) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Select
          label="Value type"
          data={VALUE_TYPE_OPTIONS}
          value={(field.value as string | null) ?? 'none'}
          onChange={(v) => field.onChange(v ?? 'none')}
          disabled={disabled}
          allowDeselect={false}
        />
      )}
    />
  ),
};

export const OBLIGATION_FORM_FIELDS: FieldRegistry<
  ObligationInputSchema,
  ObligationFormFieldName,
  ObligationFormContext
> = {
  name: textField('Name', 12, { required: true }),

  applies_to_lease: checkboxField('Applies to leases', 4),
  applies_to_contract: checkboxField('Applies to contracts', 4),
  applies_to_well: checkboxField('Applies to wells', 4),

  value_type: valueTypeField,

  attention: textField('Attention', 6),
  address_1: textField('Address 1', 6),
  address_2: textField('Address 2', 6),
  city: textField('City', 4),
  state: textField('State', 4),
  zip: textField('Zip', 4),
  phone: textField('Phone', 4),
  fax: textField('Fax', 4),
  email: textField('Email', 4),

  remarks: textareaField('Remarks', 12),
};

/**
 * Built-in default. Groups mirror
 * api/src/lib/obligation-form-layout-schema.ts's `DEFAULT_LAYOUT`
 * (identity / applicability / contact / notes — spec §2.1) so a
 * freshly-deployed DB renders sensibly before any admin save.
 */
export const OBLIGATION_DEFAULT_LAYOUT: LayoutDocument<ObligationFormFieldName> = {
  version: 1,
  groups: [
    {
      id: 'identity',
      title: 'Identity',
      fields: [{ name: 'name', span: 12 }],
    },
    {
      id: 'applicability',
      title: 'Applicability',
      fields: [
        { name: 'applies_to_lease', span: 4 },
        { name: 'applies_to_contract', span: 4 },
        { name: 'applies_to_well', span: 4 },
        { name: 'value_type', span: 6 },
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      fields: [
        { name: 'attention', span: 6 },
        { name: 'address_1', span: 6 },
        { name: 'address_2', span: 6 },
        { name: 'city', span: 4 },
        { name: 'state', span: 4 },
        { name: 'zip', span: 4 },
        { name: 'phone', span: 4 },
        { name: 'fax', span: 4 },
        { name: 'email', span: 4 },
      ],
    },
    {
      id: 'notes',
      title: 'Notes',
      fields: [{ name: 'remarks', span: 12 }],
    },
  ],
};
